import React, { useEffect, useState } from 'react';
import {
  Table,
  Space,
  Popconfirm,
  message,
  Popover,
  List,
  Spin,
  Modal,
  Button,
  Select,
  Input,
} from 'antd';
import {
  DeleteTwoTone,
  DownOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import get from 'lodash/get';
import { useSelector, useDispatch } from 'react-redux';
import { map } from 'lodash';
import { userMangement } from '../../redux/actions/authActions';
import AddNewUser from '../../components/user/addNewUser';
import {
  USER_REMOVED_SUCCESS,
  USER_ROLE_UPDATE_SUCCESS,
} from '../../constants/messages';
import { generatePaginationObj, hasPermission } from '../../utilities/helpers';

const { confirm } = Modal;

function TeamDetails() {
  const [loading, setLoading] = useState(false);
  const [systemRoles, setSystemRoles] = useState([]);
  const [members, setMembers] = useState({
    data: [],
    filters: {
      name: undefined,
      email: undefined,
      role_id: undefined,
      is_active: true,
    },
    sorters: {
      sortBy: 'name',
      sortDir: 'DESC',
    },
    pagination: {
      offset: 0,
      limit: 10,
      total: 0,
      current: 1,
    },
  });

  const [tempFilter, setTempFilter] = useState({
    role_id: undefined,
    name: undefined,
  });

  const { permissions } = useSelector((state) => get(state, 'auth.role'));
  const selectedCompany = useSelector((state) =>
    get(state, 'auth.selectedCompany', null)
  );
  const auth = useSelector((state) => get(state, 'auth', false));
  const dispatch = useDispatch();

  const getCompanyMembers = (page = 1, sorterObj = {}, filters = {}) => {
    setLoading(true);
    const pageObj = generatePaginationObj(
      page,
      get(members, 'pagination.limit')
    );
    dispatch(
      userMangement('list.users', {
        company_id: get(selectedCompany, 'id'),
        ...sorterObj,
        filters,
        ...pageObj,
      })
    )
      .then((result) => {
        setLoading(false);
        setMembers({
          data: get(result, 'data', []),
          filters,
          sorters: sorterObj,
          pagination: {
            ...pageObj,
            total: get(result, 'total'),
            current: page,
          },
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const refreshUserList = () => {
    getCompanyMembers();
  };

  const onDeleteUser = (id) => {
    setLoading(true);
    dispatch(
      userMangement('delete.user', {
        company_id: get(selectedCompany, 'id'),
        id,
      })
    )
      .then(() => {
        setLoading(false);
        message.success(USER_REMOVED_SUCCESS);
        refreshUserList();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const updateUserRole = (user, roleId) => {
    const payload = {
      id: get(user, 'id'),
      company_id: get(user, 'company_id'),
      email: get(user, 'email'),
      name: get(user, 'name'),
      is_active: get(user, 'is_active'),
      role_id: roleId,
    };
    setLoading(true);
    dispatch(userMangement('update.user', payload))
      .then(() => {
        setLoading(false);
        message.success(USER_ROLE_UPDATE_SUCCESS);
        refreshUserList();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchRoles = () => {
    setLoading(true);
    dispatch(userMangement('list.roles'))
      .then((result) => {
        setSystemRoles(get(result, 'data'));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanyMembers();
    fetchRoles();
  }, [selectedCompany]);

  const onConfirm = (user, role, roleId) => {
    confirm({
      title: 'Are you sure?',
      icon: <ExclamationCircleOutlined />,
      content: `You want to change user role to ${role}`,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        updateUserRole(user, roleId);
      },
    });
  };

  function RoleMenuItem({ user }) {
    return (
      <List
        size="small"
        dataSource={systemRoles}
        renderItem={(item) => (
          <List.Item
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onConfirm(user, get(item, 'name'), get(item, 'id'));
            }}
          >
            {get(item, 'name')}
          </List.Item>
        )}
      />
    );
  }

  return (
    <div className="team-detail-container">
      <Spin spinning={loading}>
        <div className="top-section">
          {hasPermission(permissions, ['PERM_ADD_USER']) && (
            <AddNewUser refreshUserList={refreshUserList} />
          )}
        </div>

        <div className="d-flex filter">
          <div className="d-flex filter-items">
            <div>
              <Select
                value={get(tempFilter, 'role_id')}
                placeholder="Responsibility"
                onChange={(value) => {
                  setTempFilter({
                    ...tempFilter,
                    role_id: value,
                  });
                }}
                options={map(systemRoles, (role) => ({
                  value: get(role, 'id'),
                  label: get(role, 'name'),
                }))}
              />
            </div>
            <div>
              <Input
                placeholder="Enter name"
                value={get(tempFilter, 'name')}
                onChange={(e) => {
                  setTempFilter({
                    ...tempFilter,
                    name: e.target.value,
                  });
                }}
              />
            </div>
          </div>
          <div className="d-flex filter-btns">
            <Button
              className="default-btn"
              block
              onClick={() => {
                setTempFilter({
                  role_id: undefined,
                  name: undefined,
                });
                getCompanyMembers();
              }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              block
              onClick={() => {
                getCompanyMembers(
                  get(members, 'pagination.current'),
                  get(members, 'sorters'),
                  {
                    ...get(members, 'filters'),
                    role_id: get(tempFilter, 'role_id'),
                    name: get(tempFilter, 'name'),
                  }
                );
              }}
            >
              Apply Filter
            </Button>
          </div>
        </div>

        <Table
          rowKey="id"
          columns={[
            {
              title: 'Member Name',
              key: 'name',
              dataIndex: 'name',
              sorter: true,
            },
            {
              title: 'Email',
              key: 'email',
              dataIndex: 'email',
            },
            {
              title: 'Responsibility',
              key: 'role',
              dataIndex: ['Role', 'name'],
            },
            {
              title: 'Action',
              key: 'action',
              render: (user) => {
                if (get(auth, 'user.id') !== get(user, 'id')) {
                  return (
                    <Space size="large">
                      {hasPermission(permissions, ['PERM_UPDATE_USER']) && (
                        <Popover
                          placement="bottomRight"
                          content={<RoleMenuItem user={user} />}
                          title="Change Role"
                          trigger="click"
                        >
                          <DownOutlined />
                        </Popover>
                      )}
                      {hasPermission(permissions, ['PERM_DELETE_USER']) && (
                        <Popconfirm
                          title="Are you sure to delete this user?"
                          onConfirm={() => {
                            onDeleteUser(get(user, 'id'));
                          }}
                          okText="Yes"
                          cancelText="No"
                        >
                          <DeleteTwoTone
                            className="table-icon"
                            twoToneColor="#009EA8"
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  );
                }
                return <></>;
              },
            },
          ]}
          dataSource={get(members, 'data')}
          pagination={{
            showSizeChanger: false,
            position: ['bottomCenter'],
            hideOnSinglePage: true,
            pageSize: get(members, 'pagination.limit'),
            total: get(members, 'pagination.total'),
            current: get(members, 'pagination.current'),
          }}
          onChange={(paginationObj, filterObj, sorterObj) => {
            getCompanyMembers(
              get(paginationObj, 'current'),
              get(sorterObj, 'order')
                ? {
                    sortBy: get(sorterObj, 'field'),
                    sortDir:
                      get(sorterObj, 'order') === 'ascend' ? 'ASC' : 'DESC',
                  }
                : {},
              get(members, 'filters')
            );
          }}
        />
      </Spin>
    </div>
  );
}

export default TeamDetails;
